/**
 * Path matcher utility for matching request paths against OpenAPI/Swagger/NestJS path patterns
 *
 * Note: Input paths should be clean request paths without query strings or hash fragments
 *
 * Supports:
 * - Exact matching: /api/users === /api/users (only)
 * - Path parameters: /api/users/{id} matches /api/users/123
 * - Nested parameters: /api/users/{userId}/posts/{postId}
 * - Wildcard (asterisk): /api/star/users matches /api/v1/users (use * for single segment)
 * - Recursive wildcard: /files/star-star matches /files/a/b/c (use two asterisks for multiple segments)
 *
 * Note: Prefix matching has been removed. Use slash-two-asterisks for matching sub-paths:
 * - /api/users - matches /api/users only
 * - /api/users/two-asterisks - matches /api/users, /api/users/123, /api/users/123/posts, etc.
 */

/**
 * Convert path pattern to regex, supporting wildcards and path parameters
 *
 * Examples:
 *   /api/users/{id} -> /^\/api\/users\/[^/]+$/
 *   /api/star/users -> /^\/api\/[^/]+\/users$/
 *   /files/double-star -> /^\/files\/.+$/
 *   /api/v{version}/users -> /^\/api\/v[^/]+\/users$/
 */
function pathPatternToRegex(pattern: string): RegExp {
  // Escape special regex characters except {}, *, /
  let regexPattern = pattern.replace(/[.+?^$|()[\]\\]/g, '\\$&');

  // Handle recursive wildcard /** (must be at the end or followed by /)
  // /files/** -> /files/.+ (matches one or more path segments)
  // This follows NestJS routing convention where ** requires at least one segment
  regexPattern = regexPattern.replace(/\/\*\*$/, '/.+');
  regexPattern = regexPattern.replace(/\/\*\*\//g, '/(?:.+/)?');

  // Handle single wildcard * (matches exactly one path segment)
  // /api/*/users -> /api/[^/]+/users
  regexPattern = regexPattern.replace(/\*/g, '[^/]+');

  // Replace path parameters {param} with regex group that matches anything except /
  regexPattern = regexPattern.replace(/\{[^}]+\}/g, '[^/]+');

  // Anchor to start and end
  return new RegExp(`^${regexPattern}$`);
}

/**
 * Check if an actual path matches an OpenAPI/Swagger/NestJS path pattern
 *
 * @param actualPath - The actual request path (e.g., /api/users/123)
 * @param pattern - The path pattern (e.g., /api/users/{id})
 * @returns true if the actual path matches the pattern
 *
 * @example
 * // Exact match
 * matchesPathPattern('/api/users', '/api/users') // true
 * matchesPathPattern('/api/users/123', '/api/users') // false
 *
 * // Path parameters
 * matchesPathPattern('/api/users/123', '/api/users/{id}') // true
 *
 * // Wildcards
 * matchesPathPattern('/api/v1/users', '/api/star/users') // true (use * not star)
 * matchesPathPattern('/api/v2/users', '/api/star/users') // true
 *
 * // Recursive wildcards (use two asterisks, not /**!)
 * matchesPathPattern('/files/a/b/c', '/files/starstar') // true (use ** not starstar)
 * matchesPathPattern('/files/x', '/files/starstar') // true
 */
export function matchesPathPattern(actualPath: string | undefined, pattern: string): boolean {
  if (!actualPath || !pattern) {
    return false;
  }

  // Normalize paths: remove trailing slashes and query strings
  const normalizedActual = normalizePathForMatching(actualPath);
  const normalizedPattern = normalizePathForMatching(pattern);

  // Exact match (no parameters or wildcards)
  if (normalizedActual === normalizedPattern) {
    return true;
  }

  // Check if pattern contains special characters (parameters, wildcards)
  if (hasSpecialPatterns(normalizedPattern)) {
    const regex = pathPatternToRegex(normalizedPattern);
    return regex.test(normalizedActual);
  }

  // No match - exact paths that are different
  return false;
}

/**
 * Extract path parameters from actual path using pattern
 *
 * Note: This only extracts named parameters {param}, not wildcards
 *
 * @param actualPath - The actual request path
 * @param pattern - The path pattern with parameters
 * @returns Object with parameter names and values, or null if no match
 *
 * @example
 * extractPathParams('/api/users/123', '/api/users/{id}')
 * // { id: '123' }
 *
 * extractPathParams('/api/users/123/posts/456', '/api/users/{userId}/posts/{postId}')
 * // { userId: '123', postId: '456' }
 *
 * // Wildcards are not captured (use asterisk)
 * extractPathParams('/api/v1/users/123', '/api/asterisk/users/{id}')
 * // { id: '123' }
 */
export function extractPathParams(
  actualPath: string,
  pattern: string,
): Record<string, string> | null {
  const normalizedActual = normalizePathForMatching(actualPath);
  const normalizedPattern = normalizePathForMatching(pattern);

  // Extract parameter names from pattern (only {param} style)
  const paramNames: string[] = [];
  const paramRegex = /\{([^}]+)\}/g;
  let match: RegExpExecArray | null;

  while ((match = paramRegex.exec(normalizedPattern)) !== null) {
    paramNames.push(match[1]);
  }

  if (paramNames.length === 0) {
    // No named parameters in pattern
    return normalizedActual === normalizedPattern ? {} : null;
  }

  // Build regex with capturing groups for named parameters
  let regexPattern = normalizedPattern.replace(/[.+?^$|()[\]\\]/g, '\\$&');

  // Replace wildcards with non-capturing groups
  regexPattern = regexPattern.replace(/\/\*\*$/, '/.+');
  regexPattern = regexPattern.replace(/\/\*\*\//g, '/(?:.+/)?');
  regexPattern = regexPattern.replace(/\*/g, '[^/]+');

  // Replace named parameters with capturing groups
  regexPattern = regexPattern.replace(/\{[^}]+\}/g, '([^/]+)');

  const regex = new RegExp(`^${regexPattern}$`);

  const result = regex.exec(normalizedActual);
  if (!result) {
    return null;
  }

  // Map parameter names to captured values
  const params: Record<string, string> = {};
  paramNames.forEach((name, index) => {
    params[name] = result[index + 1]; // index + 1 because result[0] is the full match
  });

  return params;
}

/**
 * Check if a pattern contains special matching characters
 *
 * @param pattern - The path pattern to check
 * @returns true if the pattern contains parameters, wildcards, etc.
 *
 * @example
 * hasSpecialPatterns('/api/users/{id}') // true
 * hasSpecialPatterns('/api/asterisk/users') // true (with actual asterisk character)
 * hasSpecialPatterns('/files/double-asterisk') // true (with actual double asterisk)
 * hasSpecialPatterns('/api/users') // false
 */
export function hasSpecialPatterns(pattern: string): boolean {
  return /[{*]/.test(pattern);
}

/**
 * Check if a pattern contains path parameters (not wildcards)
 *
 * @param pattern - The path pattern to check
 * @returns true if the pattern contains {param} style parameters
 *
 * @example
 * hasPathParameters('/api/users/{id}') // true
 * hasPathParameters('/api/asterisk/users') // false (with actual asterisk character)
 * hasPathParameters('/api/users') // false
 */
export function hasPathParameters(pattern: string): boolean {
  return /\{[^}]+\}/.test(pattern);
}

/**
 * Check if a pattern contains wildcards
 *
 * @param pattern - The path pattern to check
 * @returns true if the pattern contains asterisk or double asterisk
 *
 * @example
 * hasWildcards('/api/asterisk/users') // true (with actual asterisk character)
 * hasWildcards('/files/double-asterisk') // true (with actual double asterisk)
 * hasWildcards('/api/users/{id}') // false
 */
export function hasWildcards(pattern: string): boolean {
  return pattern.includes('*');
}

/**
 * Normalize a path for matching by:
 * - Removing trailing slashes
 * - Removing duplicate slashes
 *
 * @param path - The path to normalize
 * @returns Normalized path
 *
 * @example
 * normalizePathForMatching('/api/users/') // '/api/users'
 * normalizePathForMatching('/api//users') // '/api/users'
 */
export function normalizePathForMatching(path: string): string {
  return path
    .replace(/\/+/g, '/') // Replace multiple slashes with single slash
    .replace(/\/+$/, ''); // Remove trailing slash
}

/**
 * Normalize a path (deprecated, use normalizePathForMatching)
 * @deprecated Use normalizePathForMatching instead
 */
export function normalizePath(path: string): string {
  return normalizePathForMatching(path);
}
